/**
 * Firestore I/O for the unified possession matcher (possessionMatcher.js).
 *
 * Reads only what a task needs: the contracts named by the E-Check rows of the
 * plates in question (by document id, 30 per query) plus contracts that list
 * those plates directly (fallback for cars the E-Check log never saw). This
 * replaces the full-collection reads (49k airport docs) that exhausted the
 * daily Firestore quota.
 *
 * The E-Check log itself comes from archiveService (24h JSON cache).
 */
import { db } from '../firebase';
import { collection, getDocs, query, where, documentId } from 'firebase/firestore';
import { archiveService } from './archiveService';
import logger from '../utils/logger';
import { createMatcher, normalizePlate, cleanText } from './possessionMatcher';
import { USE_CONTRACTS_V2, v2RecordsByIds, v2SearchRecords, toLegacyDoc } from './contractsV2';
import { checksForContracts, checksForPlates, mapPool } from './storeData';

const contractsCol = collection(db, 'contracts');
const airportCol = collection(db, 'airport_contracts');
const IN_LIMIT = 30;

const chunks = (arr, n) => { const out = []; for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n)); return out; };

/** "W28656" -> ["W28656", "W 28656"]: the spellings stored in the documents. */
export function plateVariants(plate) {
    const norm = normalizePlate(plate);
    if (!norm) return [];
    const m = /^([A-Z]+)(\d+)$/.exec(norm);
    return m ? [norm, `${m[1]} ${m[2]}`] : [norm];
}

async function queryIn(col, field, values) {
    const docs = [];
    for (const chunk of chunks(values, IN_LIMIT)) {
        const snap = await getDocs(query(col, where(field, 'in', chunk)));
        snap.forEach(d => docs.push({ id: d.id, ...d.data() }));
    }
    return docs;
}

/** Raw documents of both contract collections for the given contract numbers (the contracts store with
 *  USE_CONTRACTS_V2: each record in its old collection's shape). */
export async function fetchContractsByIds(contractNos) {
    const ids = [...new Set((contractNos || []).map(c => cleanText(c).toUpperCase()).filter(Boolean))];
    if (!ids.length) return [];
    if (USE_CONTRACTS_V2) return (await v2RecordsByIds(ids, 'all')).map(toLegacyDoc);
    const [bb, ap] = await Promise.all([
        queryIn(contractsCol, documentId(), ids),
        queryIn(airportCol, documentId(), ids),
    ]);
    return [...bb, ...ap];
}

/** The store's contracts of one car: its search finds a plate (whole and its number), kept to that exact plate -
 *  the car it has now or the one it was opened with. */
const storeContractsOfPlate = async (plate) => {
    const key = normalizePlate(plate);
    const same = (car) => normalizePlate(car && car.plate) === key;
    return (await v2SearchRecords(key, 'all')).filter((v) => same(v.car) || same(v.first_car));
};

/** Raw documents whose plate field is one of the given plates (both spellings; the contracts store with
 *  USE_CONTRACTS_V2, 4 plates at a time). */
export async function fetchContractsByPlates(plates) {
    if (USE_CONTRACTS_V2) {
        const keys = [...new Set((plates || []).map(normalizePlate).filter(Boolean))];
        const lists = await mapPool(keys, 4, storeContractsOfPlate);
        const byNo = new Map(lists.flat().map((v) => [v.contract_no, v]));
        return [...byNo.values()].map(toLegacyDoc);
    }
    const values = [...new Set((plates || []).flatMap(plateVariants))];
    if (!values.length) return [];
    const [bb, ap] = await Promise.all([
        queryIn(contractsCol, 'plate_no', values),
        queryIn(airportCol, 'Plate No.', values),
    ]);
    return [...bb, ...ap];
}

/** Every E-Check row (cached by archiveService). */
export const loadEcheckRows = () => archiveService.load();

const rowPlate = (r) => normalizePlate(r['Plate Number'] ?? r['Plate No.'] ?? r.plate_number ?? r.plate);
const rowContract = (r) => cleanText(r['Contract No.'] ?? r.contract_no ?? r.contract).toUpperCase();

/**
 * Matcher for a set of plates (fines / parking / Salik files).
 * Pulls: E-Check rows of those plates -> their contracts -> every E-Check row of
 * those contracts (so swaps are known) -> contract docs by id and by plate.
 */
export async function buildMatcherForPlates(plates, { echeckRows, now = new Date() } = {}) {
    const wanted = new Set((plates || []).map(normalizePlate).filter(Boolean));
    let rows = echeckRows;
    if (!rows && USE_CONTRACTS_V2) {
        // the store: the checks of those cars, then every check of the contracts they were on (car swaps)
        const ofPlates = await checksForPlates([...wanted]);
        rows = [...ofPlates, ...await checksForContracts(ofPlates.map(rowContract))];
    }
    rows = rows || await loadEcheckRows();
    const contractNos = new Set();
    for (const r of rows) if (wanted.has(rowPlate(r))) { const c = rowContract(r); if (c) contractNos.add(c); }
    const relevant = rows.filter(r => wanted.has(rowPlate(r)) || contractNos.has(rowContract(r)));
    const [byId, byPlate] = await Promise.all([fetchContractsByIds([...contractNos]), fetchContractsByPlates([...wanted])]);
    logger.info(`[possessionMatcher] ${wanted.size} plates -> ${contractNos.size} contracts from E-Check, ${byId.length + byPlate.length} contract docs read`);
    return createMatcher({ contractDocs: [...byId, ...byPlate], echeckRows: relevant, now });
}

/**
 * Matcher for a set of contract numbers (mileage / multi-car): all their
 * E-Check rows plus the contract docs.
 */
export async function buildMatcherForContracts(contractNos, { echeckRows, now = new Date() } = {}) {
    const wanted = new Set((contractNos || []).map(c => cleanText(c).toUpperCase()).filter(Boolean));
    const rows = echeckRows || (USE_CONTRACTS_V2 ? await checksForContracts([...wanted]) : await loadEcheckRows());
    const relevant = rows.filter(r => wanted.has(rowContract(r)));
    const docs = await fetchContractsByIds([...wanted]);
    return createMatcher({ contractDocs: docs, echeckRows: relevant, now });
}
